import { Box, Checkbox, FormControlLabel, FormControlLabelProps, IconButton, InputAdornment, InputBase, Radio } from '@mui/material';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useStyles, ButtonStyleProps, useControlClasses } from './styles';
import KeyboardIcon from '@mui/icons-material/Keyboard';

export type ButtonStyleCheckboxProps = {
  // outside props
  label: string | React.ReactNode;
  value: string;
  other?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  color?: 'primary' | 'secondary';
  classes?: ButtonStyleProps['classes'];
  controlClasses?: FormControlLabelProps['classes'];
  control?: 'checkbox' | 'radio';
  renderOtherInput?: (props: {
    autoFocus: boolean;
    placeholder: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    value?: string;
    color: 'primary' | 'secondary';
  }) => React.ReactNode;
  otherPlacement?: 'asLabel' | 'underLabel';

  // parent props
  name?: string;
  checked?: boolean;
  otherText?: string;
  onChange?: (checked: boolean, value: string, other: boolean) => void;
  noButton?: boolean;
};


export const ButtonStyleCheckbox: React.FC<ButtonStyleCheckboxProps> = ({
  color = 'secondary',
  controlClasses: _controlClasses,
  control = 'checkbox',
  noButton = false,
  ...props
}) => {
  const [ autoFocus, setAutoFocus ] = React.useState(false);
  const { classes } = useStyles({ color, classes: props.classes, noButton });
  const { classes: controlClasses } = useControlClasses({}, { props: { classes: _controlClasses } });

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    props?.onChange?.(e.target.checked, e.target.value, props.other ?? false);
  }, [ props ]);

  const handleTextChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    props?.onChange?.(props.checked ?? false, e.target.value, props.other ?? false);
  }, [ props ]);

  React.useEffect(() => {
    // Prevent autofocus on mount, but enable autofocus on check
    setAutoFocus(true);
  }, []);

  const inputProps = useMemo(() => ({
    autoFocus,
    placeholder: 'Other',
    onChange: handleTextChange,
    value: props.otherText,
    color
  }), [ autoFocus, handleTextChange, props.otherText, color ]);

  const showOther = props.other && props.checked;

  const label: string | React.ReactNode = useMemo(() => {
    if (!showOther) return props.label;
    if (props.renderOtherInput) {
      if (props.otherPlacement === 'asLabel') {
        return props.renderOtherInput(inputProps);
      } else {
        return inputProps.placeholder;
      }
    }
    return (
      <InputBase
        {...inputProps}
        endAdornment={(
          <InputAdornment position="end">
            <IconButton color={color}>
              <KeyboardIcon />
            </IconButton>
          </InputAdornment>
        )}
      />
    );
  }, [ color, inputProps, props, showOther ]);

  const controlProps = {
    name: props.name,
    value: props.value,
    color: color,
    className: classes.checkbox,
    disabled: props.disabled,
    onChange: handleChange
  };

  return (
    <>
      <FormControlLabel
        classes={controlClasses}
        className={noButton ? undefined : clsx(
          noButton ? undefined : classes.buttonStyle,
          props.checked && (props.other ? classes.otherSelected : classes.selected)
        )}
        checked={props.checked}
        label={label}
        disabled={props.disabled}
        control={control === 'checkbox' ? <Checkbox {...controlProps} /> : <Radio {...controlProps}/>}
      />
      {showOther && props.renderOtherInput && props.otherPlacement === 'underLabel' && (
        <Box display="flex">
          <div className={classes.checkboxOffset} />
          {props.renderOtherInput(inputProps)}
        </Box>
      )}
    </>
  );
};
